'use client'

import { useState, useEffect, useRef, useCallback, type MouseEvent, type ReactNode } from "react"
import { createClient } from "@/lib/supabase/client"
import { sanitizeNextPath } from "@/lib/auth/redirect"
import { captureSignupAttributionClient } from "@/lib/growth/signup-attribution"
import { Wordmark } from "@/components/brand"

/* ═══════════════════════════════════════════════════════════════════
MINDHUNTERS — MARKETING SITE v8 — FINAL CANON
Fog palette. Fraunces. Conversion-first + brand premium. "One place" framework. Login/Signup CTAs. No waitlist.
Copy: FINAL (audited + optimized)
═══════════════════════════════════════════════════════════════════ */

const T = {
bg0:"#F4F5F7",bg1:"#EAEBEE",s1:"#E0E1E4",card:"#FFFFFF",
cardBorder:"rgba(0,0,0,0.06)",border:"rgba(0,0,0,0.06)",borderStrong:"rgba(0,0,0,0.12)",
text1:"#0F1011",text2:"rgba(15,16,17,0.62)",text3:"rgba(15,16,17,0.44)",textMuted:"rgba(15,16,17,0.28)",
accent:"#0F1011",accentText:"#F4F5F7",accentMuted:"rgba(15,16,17,0.04)",
espresso:"#8B6F4E",espressoMuted:"rgba(139,111,78,0.12)",espressoSubtle:"rgba(139,111,78,0.04)",espressoGlow:"rgba(139,111,78,0.06)",
success:"#0D8C5B",warning:"#D4A017",
shadowSm:"0 1px 2px rgba(0,0,0,0.03),0 0 0 1px rgba(0,0,0,0.02)",
shadowMd:"0 2px 8px rgba(0,0,0,0.05),0 0 0 1px rgba(0,0,0,0.03)",
shadowHero:"0 20px 60px rgba(0,0,0,0.10),0 0 0 1px rgba(0,0,0,0.04)",
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400;1,9..144,500&family=Inter:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
html, body, #root { min-height: 100%; }
body {
font-family: 'Inter', sans-serif;
background: ${T.bg0};
color: ${T.text1};
-webkit-font-smoothing: antialiased;
overflow-x: hidden;
}
::selection { background: ${T.espressoMuted}; color: ${T.text1}; }
button { cursor: pointer; font-family: inherit; border: none; background: none; }
input { font-family: inherit; }
a { text-decoration: none; color: inherit; }

.grain { position: fixed; inset: 0; pointer-events: none; z-index: 0; opacity: 0.018; }

@keyframes enter { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
@keyframes breathe { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
@keyframes shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
@keyframes gridPulse { 0%, 100% { opacity: 0.025; } 50% { opacity: 0.05; } }
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
@keyframes expandIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
@keyframes orbit { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes orbitR { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
@keyframes countUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes fadeFloat { 0%, 100% { opacity: 0.4; transform: translateY(0); } 50% { opacity: 0.7; transform: translateY(-4px); } }

.in { animation: enter 700ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.expand { animation: expandIn 800ms cubic-bezier(0.16, 1, 0.3, 1) both; }

.shimmer-text {
background: linear-gradient(110deg, ${T.espresso} 20%, #C4A882 45%, #E8D5B8 50%, #C4A882 55%, ${T.espresso} 80%);
background-size: 200% 100%;
-webkit-background-clip: text; background-clip: text;
-webkit-text-fill-color: transparent;
animation: shimmer 4s linear infinite;
}

.serif {
font-family: 'Fraunces', Georgia, serif;
font-style: normal;
font-variation-settings: "opsz" 144, "SOFT" 100, "wght" 400;
font-weight: 400;
letter-spacing: -0.04em;
}
.s { max-width: 1080px; margin: 0 auto; padding: 0 24px; }

.hero-grid {
position: absolute; inset: 0;
background-size: 60px 60px;
background-image:
linear-gradient(to right, rgba(0,0,0,0.025) 1px, transparent 1px),
linear-gradient(to bottom, rgba(0,0,0,0.025) 1px, transparent 1px);
mask-image: radial-gradient(ellipse 70% 60% at 50% 35%, black 0%, transparent 70%);
-webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 35%, black 0%, transparent 70%);
animation: gridPulse 8s ease infinite;
}

.mockup {
background: ${T.card}; border: 1px solid ${T.cardBorder}; border-radius: 14px;
overflow: hidden; box-shadow: ${T.shadowHero};
transition: transform 500ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 400ms ease;
}
.mockup:hover {
transform: translateY(-4px);
box-shadow: 0 24px 70px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04);
}
.mockup-bar {
height: 28px; display: flex; align-items: center; gap: 5px; padding: 0 10px;
background: ${T.bg0}; border-bottom: 1px solid ${T.border};
}
.mockup-dot { width: 7px; height: 7px; border-radius: 99px; }

@media (max-width: 767px) {
.s { padding: 0 16px; }
.hero-cols { flex-direction: column !important; }
.pillars-grid { grid-template-columns: 1fr !important; }
.one-place-grid { grid-template-columns: 1fr 1fr !important; }
.surface-grid { grid-template-columns: 1fr 1fr !important; }
.flow-cols { flex-direction: column !important; gap: 20px !important; }
.panels-stage { height: 360px !important; }
.watermark-desktop { display: none !important; }
}
@media (prefers-reduced-motion: reduce) {
*, *::before, *::after { animation: none !important; transition: none !important; }
}
`;

/* ═══════ ATOMS ═══════ */

function Grain() {
return (
<div className="grain">
<svg width="100%" height="100%">
<filter id="gn"><feTurbulence baseFrequency=".82" numOctaves="4" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
<rect width="100%" height="100%" filter="url(#gn)" />
</svg>
</div>
);
}

function Dots({ size = 16, breathing = true }: { size?: number; breathing?: boolean }) {
const a = (d: number) => breathing
? { fill: T.espresso, animation: `breathe 3s ease-in-out infinite ${d}s` }
: { fill: T.espresso, opacity: d === 0 ? 1 : d === 0.5 ? 0.5 : 0.25 };
return (
<svg width={size} height={size} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
<circle cx="6" cy="6" r="3" style={a(0)} />
<circle cx="14" cy="6" r="3" style={a(0.5)} />
<circle cx="10" cy="14" r="3" style={a(1)} />
</svg>
);
}

/* ═══════ ORBITAL VISUAL (hero background) ═══════ */

function OrbitalVisual() {
const rings = [
{ s: 280, d: 45, dots: [0, 120, 240] },
{ s: 400, d: 60, dots: [30, 150, 270], rev: true },
{ s: 520, d: 80, dots: [60, 180, 300] },
];
return (
<div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, pointerEvents: "none" }}>
<svg width={600} height={600} style={{ position: "absolute", opacity: 0.04 }}>
{[0, 60, 120, 180, 240, 300].map((a) => {
const r = (a * Math.PI) / 180;
return <line key={a} x1={300} y1={300} x2={300 + Math.cos(r) * 280} y2={300 + Math.sin(r) * 280} stroke={T.text1} strokeWidth={0.5} />;
})}
</svg>
{rings.map((ring, ri) => (
<div key={ri} style={{
position: "absolute", top: "50%", left: "50%",
width: ring.s, height: ring.s, marginLeft: -ring.s / 2, marginTop: -ring.s / 2,
borderRadius: "50%", border: "1px solid rgba(139,111,78,0.06)",
animation: `${ring.rev ? "orbitR" : "orbit"} ${ring.d}s linear infinite`,
}}>
{ring.dots.map((angle, di) => {
const r = (angle * Math.PI) / 180;
return (
<div key={di} style={{
position: "absolute",
left: ring.s / 2 + Math.cos(r) * (ring.s / 2) - 3,
top: ring.s / 2 + Math.sin(r) * (ring.s / 2) - 3,
width: 4 + di, height: 4 + di, borderRadius: 99,
background: T.espresso, opacity: 0.15 + di * 0.15,
animation: `fadeFloat ${3 + di}s ease infinite ${di * 0.5}s`,
}} />
);
})}
</div>
))}
<div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 120, height: 120, borderRadius: "50%", background: `radial-gradient(circle,${T.espressoGlow},transparent 70%)`, filter: "blur(20px)" }} />
</div>

);
}

/* ═══════ STACKED PANELS (interactive 3D) ═══════ */

function StackedPanels() {
const ref = useRef<HTMLDivElement | null>(null);
const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
const [hov, setHov] = useState(false);
const tgt = useRef({ x: 0.5, y: 0.5 });
const cur = useRef({ x: 0.5, y: 0.5 });
const raf = useRef<number | null>(null);

const panels = [
{ l: "Channels", a: false },
{ l: "Campaigns", a: false },
{ l: "Journeys", a: true },
{ l: "Products", a: false },
{ l: "Systems", a: true },
{ l: "Insights", a: false },
{ l: "Hunter", a: true },
];
const N = panels.length, ZS = 55, SIG = 1.8;

// Growth Loop 1 — capture first-touch signup attribution (powered-by UTM).
useEffect(() => { captureSignupAttributionClient(); }, []);

useEffect(() => {
const go = () => {
const c = cur.current, t = tgt.current;
c.x += (t.x - c.x) * 0.08;
c.y += (t.y - c.y) * 0.08;
setMouse({ x: c.x, y: c.y });
raf.current = requestAnimationFrame(go);
};
raf.current = requestAnimationFrame(go);
return () => {
if (raf.current !== null) cancelAnimationFrame(raf.current);
};
}, []);

const onMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
if (!ref.current) return;
const r = ref.current.getBoundingClientRect();
tgt.current = { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
}, []);

const rY = -40 + (mouse.x - 0.5) * 16;
const rX = 16 + (mouse.y - 0.5) * -10;

return (
<div ref={ref} onMouseMove={onMove} onMouseEnter={() => setHov(true)} onMouseLeave={() => { setHov(false); tgt.current = { x: 0.5, y: 0.5 }; }}
style={{ perspective: 1200, position: "relative", width: "100%", height: 480, display: "flex", alignItems: "center", justifyContent: "center", cursor: "default", userSelect: "none" }}>
<div style={{ transform: `rotateY(${rY}deg) rotateX(${rX}deg)`, transformStyle: "preserve-3d", position: "relative", width: 0, height: 0, transition: "transform 700ms cubic-bezier(0.16,1,0.3,1)" }}>
{panels.map((p, i) => {
const t = i / (N - 1);
const bz = (i - (N - 1)) * ZS;
const w = 170 + t * 110, h = 240 + t * 90;
const op = 0.3 + t * 0.7;
const cp = mouse.x * (N - 1);
const dist = Math.abs(i - cp);
const inf = Math.exp(-(dist * dist) / (2 * SIG * SIG));
const ly = hov ? -inf * 65 : 0;
const sy = hov ? (0.35 + inf * 0.65) : 1;
const bg = p.a ? "rgba(139,111,78,0.03)" : "rgba(15,16,17,0.01)";
const bd = p.a ? "rgba(139,111,78,0.08)" : "rgba(0,0,0,0.04)";

      return (
        <div key={i} style={{
          position: "absolute", borderRadius: 10, overflow: "hidden",
          background: `linear-gradient(180deg,${T.card},${bg})`,
          border: `1px solid ${bd}`,
          boxShadow: "0 4px 20px rgba(0,0,0,0.04),0 0 0 1px rgba(0,0,0,0.02)",
          width: w, height: h, marginLeft: -w / 2, marginTop: -h / 2,
          transform: `translateZ(${bz}px) translateY(${ly}px) scaleY(${sy})`,
          transformOrigin: "bottom center", opacity: op,
          transition: "transform 700ms cubic-bezier(0.16,1,0.3,1),opacity 400ms ease",
          willChange: "transform,opacity",
        }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg,transparent 10%,rgba(255,255,255,${0.4 + t * 0.4}) 50%,transparent 90%)` }} />
          <div style={{ padding: 14, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div>
              <div style={{ width: w * 0.5, height: 3, borderRadius: 2, background: T.s1, marginBottom: 6 }} />
              <div style={{ width: w * 0.35, height: 2, borderRadius: 2, background: T.bg1 }} />
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {[0.7, 0.5, 0.9, 0.3].map((v, j) => (
                <div key={j} style={{ flex: 1, height: 28, borderRadius: 3, background: T.bg1, position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: `${v * 100}%`, borderRadius: 3, background: p.a ? T.espressoSubtle : T.accentMuted }} />
                </div>
              ))}
            </div>
            <span style={{ fontSize: 9, fontWeight: 600, color: T.text3, letterSpacing: ".02em", opacity: t > 0.3 ? 1 : 0.4 }}>{p.l}</span>
          </div>
        </div>
      );
    })}
  </div>
  <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", fontSize: 10, color: T.textMuted, opacity: hov ? 0 : 0.5, transition: "opacity 300ms" }}>
    Hover to explore
  </div>
</div>

);
}

/* ═══════ PRODUCT MOCKUPS ═══════ */

function MockupFrame({ children, height = 280, label = "mindhunters.io" }: { children: ReactNode; height?: number; label?: string }) {
return (
<div className="mockup">
<div className="mockup-bar">
<div className="mockup-dot" style={{ background: "#FF5F57" }} />
<div className="mockup-dot" style={{ background: "#FFBD2E" }} />
<div className="mockup-dot" style={{ background: "#28C840" }} />
<div style={{ flex: 1 }} />
<span style={{ fontSize: 9, color: T.textMuted }}>{label}</span>
<div style={{ flex: 1 }} />
</div>
<div style={{ height, background: T.bg0, position: "relative", overflow: "hidden" }}>{children}</div>
</div>
);
}

function CanvasMock() {
const m = [
{ l: "LEADS", v: "124", d: "+18%", u: true },
{ l: "REVENUE", v: "$4.2k", d: "+12%", u: true },
{ l: "CONV", v: "23%", d: "-2%", u: false },
{ l: "RESP", v: "8m", d: "-34%", u: true },
];
const sys = [
{ n: "Cold Lead Recovery", a: true, m: "28 recovered" },
{ n: "Cart Recovery", a: true, m: "22.4% conv" },
{ n: "WA Qualification", a: false, m: "Fix needed" },
];
return (
<div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10, height: "100%" }}>
<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
<Dots size={10} breathing />
<span style={{ fontSize: 11, fontWeight: 500, color: T.text2 }}>Good morning</span>
<span style={{ fontSize: 9, color: T.textMuted, marginLeft: "auto" }}>3 systems</span>
</div>
<div style={{ display: "flex", padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
{m.map((x, i) => (
<div key={i} style={{ flex: 1, padding: "0 8px", borderRight: i < 3 ? `1px solid ${T.border}` : "none" }}>
<span style={{ fontSize: 7, fontWeight: 700, color: T.textMuted, letterSpacing: ".06em" }}>{x.l}</span>
<div style={{ display: "flex", alignItems: "baseline", gap: 3, marginTop: 1 }}>
<span style={{ fontSize: 14, fontWeight: 600, color: T.text1, fontVariantNumeric: "tabular-nums" }}>{x.v}</span>
<span style={{ fontSize: 8, fontWeight: 500, color: x.u ? T.success : T.warning }}>{x.d}</span>
</div>
</div>
))}
</div>
<div>
{sys.map((s, i) => (
<div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", marginTop: 3, background: T.card, borderRadius: 6, border: `1px solid ${T.cardBorder}` }}>
<div style={{ width: 4, height: 4, borderRadius: 99, background: s.a ? T.success : T.warning }} />
<span style={{ fontSize: 10, fontWeight: 500, color: T.text1, flex: 1 }}>{s.n}</span>
<span style={{ fontSize: 9, color: T.text3 }}>{s.m}</span>
</div>
))}
</div>
<div style={{ background: T.espressoSubtle, border: "1px solid rgba(139,111,78,0.06)", borderRadius: 8, padding: "8px 10px", marginTop: "auto" }}>
<div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
<Dots size={7} breathing />
<span className="shimmer-text" style={{ fontSize: 7, fontWeight: 700, letterSpacing: ".05em" }}>HUNTER BRIEF</span>
</div>
<p style={{ fontSize: 9, color: T.text2, lineHeight: 1.5 }}>Cart Recovery outperforming at 22.4%. 3 leads need response.</p>
</div>
</div>
);
}

function HunterMock() {
return (
<div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", height: "100%" }}>
<div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
<Dots size={8} breathing />
<span style={{ fontSize: 10, fontWeight: 600, color: T.text1 }}>Build skincare quiz</span>
<span style={{ fontSize: 8, color: T.espresso, marginLeft: "auto", background: T.espressoSubtle, padding: "2px 6px", borderRadius: 3 }}>7 cr</span>
</div>
<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
<div style={{ alignSelf: "flex-end", background: T.accentMuted, borderRadius: "8px 8px 2px 8px", padding: "6px 10px", maxWidth: "72%" }}>
<p style={{ fontSize: 10, color: T.text1 }}>Create a skincare recommendation quiz</p>
</div>
<div style={{ maxWidth: "80%" }}>
<p style={{ fontSize: 10, color: T.text2, lineHeight: 1.6 }}>Building a 5-question quiz. Working on it now…</p>
</div>
<div style={{ background: T.card, borderRadius: 6, padding: "6px 10px", border: `1px solid ${T.cardBorder}` }}>
{["Analyzing catalog", "Generating questions", "Matching recommendations"].map((s, i) => (
<div key={i} style={{ display: "flex", alignItems: "center", gap: 5, marginTop: i ? 3 : 0 }}>
<div style={{ width: 3, height: 3, borderRadius: 99, background: i < 2 ? T.success : T.espresso }} />
<span style={{ fontSize: 9, color: i < 2 ? T.text3 : T.text2 }}>{s}</span>
</div>
))}
</div>
</div>
<div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
<Dots size={7} breathing />
<span style={{ fontSize: 10, color: T.textMuted, flex: 1 }}>Continue…</span>
<div style={{ width: 20, height: 20, borderRadius: 99, background: T.espresso, display: "flex", alignItems: "center", justifyContent: "center" }}>
<svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5} strokeLinecap="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
</div>
</div>
</div>
);
}

/* ═══════ EMAIL CAPTURE ═══════ */

function AuthInput({ compact = false }: { compact?: boolean }) {
const supabase = createClient()
const [email, setEmail] = useState("")
const [status, setStatus] = useState<"idle" | "loading" | "sent">("idle") // idle | loading | sent
const [error, setError] = useState("")

const callbackRedirectTo = (() => {
if (typeof window === "undefined") return "/auth/callback"
const safeNext = sanitizeNextPath(new URLSearchParams(window.location.search).get("next"))
return `${window.location.origin}/auth/callback?next=${encodeURIComponent(safeNext)}`
})()

const handleSubmit = async () => {
const trimmed = email.trim().toLowerCase()
if (!trimmed || !trimmed.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
setError("Enter a valid email")
return
}
setStatus("loading")
setError("")
const { error: signInError } = await supabase.auth.signInWithOtp({
email: trimmed,
options: { emailRedirectTo: callbackRedirectTo },
})
if (!signInError) setStatus("sent")
else {
setStatus("idle")
setError("Unable to send link. Try again.")
}
}

const handleGoogle = async () => {
await supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: callbackRedirectTo } })
}

if (status === "sent") {
return (
<div style={{
display: "inline-flex", alignItems: "center", gap: 8,
background: "rgba(13,140,91,0.06)", border: "1px solid rgba(13,140,91,0.10)",
borderRadius: 10, padding: "12px 20px",
}}>
<svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#0D8C5B" strokeWidth={2.5} strokeLinecap="round">
<polyline points="20 6 9 17 4 12" />
</svg>
<span style={{ fontSize: 13, fontWeight: 500, color: "#0D8C5B" }}>Check your email for the login link.</span>
</div>
)
}

return (
<div style={{ maxWidth: compact ? 360 : 400 }}>
<div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
<input
type="email"
value={email}
onChange={(e) => { setEmail(e.target.value); setError(""); }}
onKeyDown={(e) => { if (e.key === "Enter") void handleSubmit(); }}
placeholder="you@company.com"
disabled={status === "loading"}
style={{
flex: 1, height: 46, padding: "0 16px", fontSize: 13,
borderRadius: 10, border: `1px solid ${error ? "#C4342E" : T.borderStrong}`,
background: T.card, color: T.text1, outline: "none",
transition: "border 150ms", boxShadow: T.shadowSm,
opacity: status === "loading" ? 0.7 : 1,
}}
onFocus={(e) => { if (!error) e.currentTarget.style.borderColor = T.espresso; }}
onBlur={(e) => { if (!error) e.currentTarget.style.borderColor = T.borderStrong; }}
/>
<button onClick={() => void handleSubmit()} disabled={status === "loading"} style={{
height: 46, padding: "0 22px", fontSize: 13, fontWeight: 600,
color: T.accentText, background: T.accent, borderRadius: 10,
boxShadow: T.shadowSm, whiteSpace: "nowrap",
opacity: status === "loading" ? 0.7 : 1,
cursor: status === "loading" ? "wait" : "pointer",
display: "flex", alignItems: "center", gap: 6,
}}>
{status === "loading" ? (
<svg width={16} height={16} viewBox="0 0 24 24" style={{ animation: "spin 1s linear infinite" }}>
<circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" fill="none" />
<path d="M12 2a10 10 0 019.95 9" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" />
</svg>
) : "Continue"}
</button>
</div>

{error && <p style={{ fontSize: 11, color: "#C4342E", marginBottom: 8 }}>{error}</p>}

<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
<div style={{ flex: 1, height: 1, background: T.border }} />
<span style={{ fontSize: 10, color: T.textMuted }}>or</span>
<div style={{ flex: 1, height: 1, background: T.border }} />
</div>

<button onClick={() => void handleGoogle()} style={{
display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
width: "100%", height: 46, borderRadius: 10,
border: `1px solid ${T.borderStrong}`, background: T.card,
fontSize: 13, fontWeight: 500, color: T.text1,
textDecoration: "none", boxShadow: T.shadowSm,
transition: "all 120ms", cursor: "pointer",
}}>
<svg width={18} height={18} viewBox="0 0 24 24">
<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
</svg>
Continue with Google
</button>
</div>
)
}

/* ═══════ AUTH MODAL ═══════ */

function AuthModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(15,16,17,0.45)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "enter 200ms cubic-bezier(0.16,1,0.3,1) both",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          background: T.card, border: `1px solid ${T.cardBorder}`,
          borderRadius: 14, padding: "32px 28px 28px",
          boxShadow: T.shadowHero,
          maxWidth: 440, width: "100%",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 30, height: 30, borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: T.text3, transition: "background 120ms, color 120ms",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = T.bg1; e.currentTarget.style.color = T.text1; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.text3; }}
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>

        <h3 className="serif" style={{ fontSize: 26, color: T.text1, marginBottom: 6, letterSpacing: "-0.03em" }}>
          Start free
        </h3>
        <p style={{ fontSize: 13, color: T.text2, marginBottom: 20, lineHeight: 1.5 }}>
          No credit card. Hunter maps your business in 2 minutes.
        </p>

        <AuthInput />
      </div>
    </div>
  );
}

function Nav() {
const [sc, setSc] = useState(false);
const [open, setOpen] = useState(false);

useEffect(() => {
const h = () => setSc(window.scrollY > 10);
window.addEventListener("scroll", h);
return () => window.removeEventListener("scroll", h);
}, []);

// Close menu on scroll
useEffect(() => {
if (open) {
const close = () => setOpen(false);
window.addEventListener("scroll", close);
return () => window.removeEventListener("scroll", close);
}
}, [open]);

return (
<nav style={{
position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
height: 52, display: "flex", alignItems: "center", justifyContent: "space-between",
padding: "0 24px",
background: sc || open ? "rgba(244,245,247,0.92)" : "transparent",
backdropFilter: sc || open ? "blur(16px) saturate(180%)" : "none",
borderBottom: sc ? `1px solid ${T.border}` : "1px solid transparent",
transition: "all 200ms ease",
}}>
{/* Wordmark */}
<a href="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", paddingLeft: 4 }}>
<Wordmark size={22} style={{ color: T.text1 }} />
</a>

  {/* Hamburger */}
  <button onClick={() => setOpen(!open)} style={{
    width: 32, height: 32, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: open ? 0 : 5,
    padding: 0, background: "none", border: "none", cursor: "pointer",
    position: "relative",
  }}>
    <div style={{
      width: 18, height: 1.5, background: T.text1, borderRadius: 1,
      transition: "all 200ms ease",
      transform: open ? "rotate(45deg) translateY(0.75px)" : "none",
      position: open ? "absolute" : "relative",
    }} />
    <div style={{
      height: 1.5, background: T.text3, borderRadius: 1,
      transition: "all 200ms ease",
      transform: open ? "rotate(-45deg) translateY(-0.75px)" : "none",
      width: open ? 18 : 14,
      position: open ? "absolute" : "relative",
    }} />
  </button>

  {/* Dropdown */}
  {open && (
    <div style={{
      position: "absolute", top: 52, right: 16,
      background: T.card, border: `1px solid ${T.cardBorder}`,
      borderRadius: 10, padding: "6px",
      boxShadow: T.shadowMd, minWidth: 160,
      animation: "enter 200ms cubic-bezier(0.16,1,0.3,1) both",
    }}>
      {[
        { label: "Features", href: "#how-it-works" },
        { label: "Pricing", href: "/pricing" },
        { label: "Log in", href: "/login" },
      ].map((item, i) => (
        <a key={i} href={item.href} onClick={() => setOpen(false)} style={{
          display: "block", padding: "10px 14px",
          fontSize: 13, fontWeight: 500,
          color: i === 2 ? T.text1 : T.text2,
          textDecoration: "none", borderRadius: 6,
          transition: "background 120ms",
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = T.bg1; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          {item.label}
        </a>
      ))}
    </div>
  )}
</nav>

);
}

/* ═══════════════════════════════════════════
2. HERO
═══════════════════════════════════════════ */

function Hero() {
  return (
    <section style={{
      position: "relative", paddingTop: 130, paddingBottom: 50, overflow: "hidden",
      minHeight: "90vh", display: "flex", alignItems: "center",
    }}>
      <div className="hero-grid" />
      <OrbitalVisual />

      <div className="s" style={{ position: "relative", zIndex: 1, width: "100%" }}>
        <div style={{ textAlign: "center", maxWidth: 800, margin: "0 auto" }}>
          {/* Headline */}
          <h1 className="in serif" style={{
            fontSize: "clamp(40px, 8vw, 76px)",
            lineHeight: 1.0,
            color: T.text1,
            marginBottom: 16,
            textAlign: "center",
          }}>
            One place to run<br />
            your business.<br />
            <span className="shimmer-text">That runs it for you.</span>
          </h1>

          {/* Subtitle */}
          <p className="in" style={{
            fontSize: "clamp(15px, 2.2vw, 18px)",
            color: T.text2,
            lineHeight: 1.6,
            maxWidth: 460,
            margin: "0 auto 32px",
            textAlign: "center",
            animationDelay: "150ms",
          }}>
            Every channel. Every system. One brain.<br />
            Hunter finds your formula and executes it.
          </p>

          {/* CTA */}
          <div className="in" style={{ display: "flex", justifyContent: "center", animationDelay: "300ms" }}>
            <AuthInput />
          </div>

          {/* Social proof */}
          <div className="in" style={{ marginTop: 24, animationDelay: "400ms", display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex" }}>
              {[0.9, 0.7, 0.5].map((op, i) => (
                <div key={i} style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: T.espresso, opacity: op,
                  border: "2px solid " + T.bg0,
                  marginLeft: i > 0 ? -6 : 0,
                }} />
              ))}
            </div>
            <span style={{ fontSize: 11, color: T.text3 }}>Founders and operators are already inside.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
2b. PRODUCT PREVIEW — Operating Canvas mockup
═══════════════════════════════════════════ */

function ProductPreview() {
  return (
    <section id="preview" style={{ padding: "20px 0 70px", overflow: "hidden" }}>
      <div className="s">
        <div className="expand hero-cols" style={{ display: "flex", gap: 16, animationDelay: "200ms" }}>
          <div style={{ flex: 1.2 }}>
            <MockupFrame height={280} label="Home — Operating Canvas"><CanvasMock /></MockupFrame>
          </div>
          <div style={{ flex: 0.8 }}>
            <MockupFrame height={280} label="Hunter — Execution"><HunterMock /></MockupFrame>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
3. WHAT YOU GET (micro-section)
═══════════════════════════════════════════ */

function OnePlace() {
const items = [
{ label: "One inbox", desc: "WhatsApp, Instagram, Email. Hunter responds when you can't." },
{ label: "One engine", desc: "Campaigns that send, qualify, and follow up — automatically." },
{ label: "One surface", desc: "Lead magnets, quizzes, and pages. Built by Hunter, not by you." },
{ label: "One brain", desc: "What changed. Why it matters. What to do next." },
];
return (
<section style={{ padding: "50px 0", borderTop: `1px solid ${T.border}` }}>
<div className="s">
<div className="one-place-grid" style={{
display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12,
}}>
{items.map((item, i) => (
<div key={i} style={{
background: T.card, border: `1px solid ${T.cardBorder}`,
borderRadius: 10, padding: "18px 16px", boxShadow: T.shadowSm,
}}>
<p className="serif" style={{ fontSize: 17, color: T.text1, marginBottom: 6 }}>{item.label}</p>
<p style={{ fontSize: 11, color: T.text3, lineHeight: 1.5 }}>{item.desc}</p>
</div>
))}
</div>
</div>
</section>
);
}

/* ═══════════════════════════════════════════
4. THE FLOW
═══════════════════════════════════════════ */

function TheFlow() {
const steps = [
{ n: "01", w: "Diagnose", d: "Hunter finds the real bottleneck." },
{ n: "02", w: "Install", d: "Pick a system. It starts running." },
{ n: "03", w: "Run", d: "Campaigns send. Leads qualify. Automatically." },
{ n: "04", w: "Improve", d: "Hunter finds what stalled and fixes it." },
];
return (
<section style={{ padding: "70px 0", borderTop: `1px solid ${T.border}`, background: T.card }}>
<div className="s">
<div style={{ textAlign: "center", marginBottom: 40 }}>
<h2 className="serif" style={{ fontSize: "clamp(24px,4vw,36px)", color: T.text1, marginBottom: 8 }}>
Diagnose. Install. Run. Improve.
</h2>
<p style={{ fontSize: 13, color: T.text3 }}>Business has a formula. Hunter finds yours.</p>
</div>
<div className="flow-cols" style={{ display: "flex", gap: 0 }}>
{steps.map((s, i) => (
<div key={i} style={{ flex: 1, padding: "0 20px", borderRight: i < 3 ? `1px solid ${T.border}` : "none" }}>
<span className="shimmer-text" style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", display: "block", marginBottom: 10 }}>{s.n}</span>
<p className="serif" style={{ fontSize: 20, color: T.text1, marginBottom: 6 }}>{s.w}</p>
<p style={{ fontSize: 12, color: T.text3, lineHeight: 1.5 }}>{s.d}</p>
</div>
))}
</div>
</div>
</section>
);
}

/* ═══════════════════════════════════════════
4a. PROOF POINTS — FAQ gold surfaced
═══════════════════════════════════════════ */

function ProofPoints() {
  const items = [
    { q: "Do I need a team?", a: "Hunter is the team." },
    { q: "Do I set everything up?", a: "Tell Hunter. It installs and starts." },
    { q: "Replace my tools?", a: "Your channels stay. The chaos goes." },
  ];
  return (
    <section style={{ padding: "60px 0", borderTop: `1px solid ${T.border}` }}>
      <div className="s">
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 12,
        }}>
          {items.map((item, i) => (
            <div key={i} style={{
              background: T.card, border: `1px solid ${T.cardBorder}`,
              borderRadius: 12, padding: "22px 20px", boxShadow: T.shadowSm,
            }}>
              <p style={{
                fontSize: 10, fontWeight: 700, color: T.espresso,
                letterSpacing: ".06em", textTransform: "uppercase",
                marginBottom: 10,
              }}>
                {item.q}
              </p>
              <p className="serif" style={{ fontSize: 22, color: T.text1, lineHeight: 1.25 }}>
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
4b. CONNECTS TO — integration logos with connect feel
═══════════════════════════════════════════ */

function ConnectsTo() {
const [active, setActive] = useState<number | null>(null);

const integrations = [
{ name: "WhatsApp", cat: "Messaging", desc: "Send and receive messages. Run campaigns. Auto-qualify leads.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none">
<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill={c}/>
<path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke={c} strokeWidth={1.2} fill="none"/>
</svg>
)},
{ name: "Instagram", cat: "Social", desc: "DMs, organic insights, content performance tracking.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
<rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1" fill={c} stroke="none"/>
</svg>
)},
{ name: "Meta Ads", cat: "Advertising", desc: "Campaign performance, spend, CPL, ROAS — synced daily.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none">
<path d="M5 12c0-3 1.5-7 3.5-7 1.5 0 2 2 3.5 5 1.5 3 2 5 3.5 5 2 0 3.5-4 3.5-7" stroke={c} strokeWidth={2.5} strokeLinecap="round" fill="none"/>
</svg>
)},
{ name: "Google Ads", cat: "Advertising", desc: "Search and display campaign metrics, keywords, quality scores.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none">
<circle cx="6" cy="18" r="3.5" fill={c}/><path d="M5.5 18L14.5 3h4l-9 15" fill={c} opacity="0.7"/><path d="M14.5 3l4 0 3 5-9 15-4 0 9-15" fill={c} opacity="0.4"/>
</svg>
)},
{ name: "TikTok Ads", cat: "Advertising", desc: "Video ad performance, conversions, audience insights.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none">
<path d="M16.6 5.82A4.278 4.278 0 0115.54 3h-3.09v12.4a2.592 2.592 0 01-2.59 2.5c-1.43 0-2.59-1.16-2.59-2.59 0-1.43 1.16-2.59 2.59-2.59.27 0 .53.04.78.12V9.68c-.26-.04-.52-.06-.78-.06A5.82 5.82 0 004 15.44 5.82 5.82 0 009.82 21.3a5.82 5.82 0 005.86-5.86V9.01a7.32 7.32 0 004.32 1.4V7.32a4.28 4.28 0 01-3.4-1.5z" fill={c}/>
</svg>
)},
{ name: "Stripe", cat: "Payments", desc: "Payments, subscriptions, checkout — international.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none">
<path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" fill={c}/>
</svg>
)},
{ name: "MercadoPago", cat: "Payments", desc: "Local payments, installments, Pix — Latin America.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
<circle cx="12" cy="12" r="9"/><path d="M8 12l2.5 2.5L16 9"/>
</svg>
)},
{ name: "Telegram", cat: "Messaging", desc: "Bot-powered conversations and lead capture.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none">
<path d="M22.05 1.577c-.393-.016-.784.08-1.117.235-.484.186-4.92 1.902-9.41 3.64-2.26.873-4.518 1.748-6.18 2.396L2.89 8.74c-.51.188-.963.525-1.09.98-.127.457.09.927.476 1.16l4.07 2.456 1.607 5.142c.15.48.503.838.93.92.427.08.876-.085 1.14-.45l2.02-2.832 4.13 3.07c.32.238.702.357 1.086.357.385 0 .77-.12 1.088-.36.635-.476.935-1.26.77-2.03L21.1 3.55c.17-.684-.09-1.408-.657-1.79-.234-.155-.51-.17-.795-.173z" fill={c}/>
</svg>
)},
{ name: "Email", cat: "Messaging", desc: "Transactional and marketing email via Resend.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
<rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/>
</svg>
)},
{ name: "Analytics", cat: "Data", desc: "Google Analytics 4 — visitor behavior and conversion tracking.", icon: (c: string) => (
<svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={1.5} strokeLinecap="round">
<path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/>
</svg>
)},
];

return (
<section style={{ padding: "60px 0", borderTop: `1px solid ${T.border}` }}>
<div className="s">
<div style={{ textAlign: "center", marginBottom: 32 }}>
<h2 className="serif" style={{ fontSize: "clamp(22px,3.5vw,30px)", color: T.text1, marginBottom: 8 }}>
Connects to everything you already use.
</h2>
<p style={{ fontSize: 13, color: T.text3, maxWidth: 400, margin: "0 auto" }}>
No migration. Mindhunters operates across your stack.
</p>
</div>

    {/* Logo grid */}
    <div style={{
      display: "flex", flexWrap: "wrap", justifyContent: "center",
      gap: 8, maxWidth: 700, margin: "0 auto",
    }}>
      {integrations.map((int, i) => {
        const isActive = active === i;
        return (
          <div key={i}
            onClick={() => setActive(isActive ? null : i)}
            style={{
              position: "relative",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
              width: 88, padding: "14px 8px",
              background: isActive ? T.espressoSubtle : T.card,
              border: `1px solid ${isActive ? "rgba(139,111,78,0.12)" : T.cardBorder}`,
              borderRadius: 10,
              cursor: "pointer",
              transition: "all 180ms ease",
              boxShadow: isActive ? T.shadowMd : T.shadowSm,
            }}
            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.borderColor = "rgba(139,111,78,0.08)"; }}
            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.borderColor = T.cardBorder; }}
          >
            <div style={{ opacity: isActive ? 1 : 0.55, transition: "opacity 150ms" }}>
              {int.icon(T.espresso)}
            </div>
            <span style={{
              fontSize: 9, fontWeight: 600, color: isActive ? T.text1 : T.text3,
              textAlign: "center", lineHeight: 1.2,
            }}>
              {int.name}
            </span>

            {/* Expand card on click */}
            {isActive && (
              <div style={{
                position: "absolute", top: "100%", left: "50%",
                transform: "translateX(-50%)",
                width: 220, marginTop: 8,
                background: T.card, border: `1px solid ${T.cardBorder}`,
                borderRadius: 10, padding: "14px 16px",
                boxShadow: T.shadowMd, zIndex: 10,
                animation: "enter 200ms ease both",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 600, color: T.espresso, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    {int.cat}
                  </span>
                </div>
                <p style={{ fontSize: 11, color: T.text2, lineHeight: 1.5, marginBottom: 10 }}>
                  {int.desc}
                </p>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 5, height: 28, fontSize: 11, fontWeight: 600,
                  color: T.accentText, background: T.accent,
                  borderRadius: 6, boxShadow: T.shadowSm,
                }}>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                  </svg>
                  Connect
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
</section>

);
}

/* ═══════════════════════════════════════════
5. PANELS — with explicit labels
═══════════════════════════════════════════ */

function PanelsSection() {
const surfaces = ["Channels", "Campaigns", "Journeys", "Products", "Systems", "Insights", "Hunter"];
return (
<section style={{ padding: "70px 0 40px", borderTop: `1px solid ${T.border}`, overflow: "hidden" }}>
<div className="s">
<div style={{ textAlign: "center", marginBottom: 8 }}>
<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginBottom: 12 }}>
<span style={{ fontSize: 9, fontWeight: 700, color: T.espresso, letterSpacing: ".06em", textTransform: "uppercase" }}>One product, every surface</span>
</div>
<h2 className="serif" style={{ fontSize: "clamp(24px,4vw,36px)", color: T.text1, marginBottom: 8 }}>
Proven systems, not isolated features.
</h2>
<p style={{ fontSize: 13, color: T.text3, maxWidth: 440, margin: "0 auto" }}>
Not seven tools. One system.
</p>
</div>
<StackedPanels />
<div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", marginTop: 16, fontSize: 11, color: T.text3 }}>
{surfaces.map((x, i) => (
<span key={i} style={{ padding: "4px 10px", background: T.accentMuted, borderRadius: 6 }}>{x}</span>
))}
</div>
</div>
</section>
);
}

/* ═══════════════════════════════════════════
6. PILLARS — with outcomes
═══════════════════════════════════════════ */

function Pillars() {
const items = [
{ t: "AGENT", n: "Hunter", d: "Not a chatbot. Your operating partner.", o: "Like Codex for your business." },
{ t: "ENGINE", n: "Proven Systems", d: "Lead recovery. Cart abandonment. Qualification. Install once.", o: "Install once. Runs forever." },
{ t: "BRAIN", n: "Causal Intelligence", d: "What changed. Why. What to do next.", o: "Know without digging." },
];
return (
<section style={{ padding: "70px 0", borderTop: `1px solid ${T.border}` }}>
<div className="s">
<div className="pillars-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
{items.map((p, i) => (
<div key={i} style={{
background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12,
padding: "24px 20px", boxShadow: T.shadowSm, transition: "all 200ms ease",
}}
onMouseEnter={(e) => { e.currentTarget.style.boxShadow = T.shadowMd; e.currentTarget.style.transform = "translateY(-2px)"; }}
onMouseLeave={(e) => { e.currentTarget.style.boxShadow = T.shadowSm; e.currentTarget.style.transform = "translateY(0)"; }}
>
<div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 14 }}>
<div style={{ width: 5, height: 5, borderRadius: 99, background: T.espresso }} />
<span style={{ fontSize: 9, fontWeight: 700, color: T.espresso, letterSpacing: ".06em", textTransform: "uppercase" }}>{p.t}</span>
</div>
<h3 className="serif" style={{ fontSize: 22, color: T.text1, marginBottom: 8 }}>{p.n}</h3>
<p style={{ fontSize: 12, color: T.text2, lineHeight: 1.6, marginBottom: 12 }}>{p.d}</p>
<p style={{ fontSize: 11, fontWeight: 600, color: T.espresso }}>{p.o}</p>
</div>
))}
</div>
</div>
</section>
);
}

/* ═══════════════════════════════════════════
6b. PRICING — Simple pricing. Honest credits.
═══════════════════════════════════════════ */

function PricingSection({ onStartFree }: { onStartFree: () => void }) {
  const [annual, setAnnual] = useState(true);

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "/mo",
      note: "Forever free",
      credits: "200 credits/mo",
      popular: false,
      features: [
        "Hunter conversations",
        "3 digital products",
        "1 journey, 1 campaign",
        "Email & chat support",
      ],
    },
    {
      name: "Pro",
      price: annual ? "$20" : "$25",
      period: "/mo",
      note: annual ? "Billed annually — save 20%" : "or $20/mo billed annually",
      credits: "12,000 credits/mo",
      popular: true,
      features: [
        "Unlimited products, journeys, campaigns",
        "AI inbox replies",
        "WhatsApp templates",
        "Priority support",
      ],
    },
    {
      name: "Max",
      price: annual ? "From $40" : "From $50",
      period: "/mo",
      note: annual ? "Scales with your multiplier · billed annually" : "Scales with your multiplier",
      credits: "24,000 – 240,000 credits/mo",
      popular: false,
      features: [
        "Everything in Pro",
        "Choose your multiplier (x2, x3, x5, x10, x20)",
        "Dedicated support",
      ],
    },
  ];

  return (
    <section id="pricing" style={{ padding: "70px 0", borderTop: `1px solid ${T.border}` }}>
      <div className="s">
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h2 className="serif" style={{ fontSize: "clamp(24px,4vw,36px)", color: T.text1, marginBottom: 8 }}>
            Simple pricing. Honest credits.
          </h2>
          <p style={{ fontSize: 13, color: T.text3, maxWidth: 440, margin: "0 auto" }}>
            Every action costs credits. You see what you use. No hidden fees.
          </p>
        </div>

        {/* Monthly / Annual toggle */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
          <div style={{ display: "flex", background: T.bg1, borderRadius: 8, padding: 3 }}>
            <button onClick={() => setAnnual(false)} style={{
              padding: "8px 20px", fontSize: 12, fontWeight: 600, borderRadius: 6,
              background: !annual ? T.card : "transparent",
              color: !annual ? T.text1 : T.text3,
              boxShadow: !annual ? T.shadowSm : "none",
              transition: "all 150ms",
            }}>Monthly</button>
            <button onClick={() => setAnnual(true)} style={{
              padding: "8px 20px", fontSize: 12, fontWeight: 600, borderRadius: 6,
              background: annual ? T.card : "transparent",
              color: annual ? T.text1 : T.text3,
              boxShadow: annual ? T.shadowSm : "none",
              transition: "all 150ms",
            }}>
              Annual <span style={{ color: T.espresso, fontSize: 10, marginLeft: 4 }}>Save 20%</span>
            </button>
          </div>
        </div>

        {/* Plan cards */}
        <div className="pillars-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, maxWidth: 960, margin: "0 auto" }}>
          {plans.map((plan, i) => (
            <div key={i} style={{
              position: "relative",
              background: T.card,
              border: `1px solid ${plan.popular ? "rgba(139,111,78,0.30)" : T.cardBorder}`,
              borderRadius: 14,
              padding: "28px 24px",
              boxShadow: plan.popular ? T.shadowMd : T.shadowSm,
            }}>
              {plan.popular && (
                <div style={{
                  position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                  background: T.espresso, color: "#fff",
                  fontSize: 9, fontWeight: 700,
                  padding: "3px 12px", borderRadius: 99,
                  letterSpacing: "0.04em", textTransform: "uppercase",
                }}>Most popular</div>
              )}

              <p style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 4 }}>{plan.name}</p>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 4 }}>
                <span className="serif" style={{ fontSize: 36, color: T.text1 }}>{plan.price}</span>
                <span style={{ fontSize: 11, color: T.text3 }}>{plan.period}</span>
              </div>
              <p style={{ fontSize: 10, color: T.textMuted, marginBottom: 10, lineHeight: 1.4 }}>{plan.note}</p>
              <p style={{ fontSize: 11, color: T.espresso, fontWeight: 600, marginBottom: 20 }}>{plan.credits}</p>

              <button
                onClick={onStartFree}
                style={{
                  display: "block", width: "100%", textAlign: "center",
                  padding: "10px 0", fontSize: 13, fontWeight: 600, borderRadius: 8,
                  marginBottom: 20, transition: "all 120ms",
                  ...(plan.popular
                    ? { background: T.accent, color: T.accentText, boxShadow: T.shadowSm }
                    : { background: "transparent", color: T.text1, border: `1px solid ${T.borderStrong}` }),
                }}
              >
                Start free
              </button>

              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                {plan.features.map((f, fi) => (
                  <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth={2.5} strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span style={{ fontSize: 12, color: T.text2, lineHeight: 1.4 }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════
7. OBJECTIONS
═══════════════════════════════════════════ */

function Objections() {
const items = [
{ q: "Is this another dashboard?", a: "No. Dashboards show. Mindhunters runs." },
{ q: "Do I need to set everything up?", a: "No. Tell Hunter what you need. It installs and starts." },
{ q: "Does it replace my tools?", a: "No. It connects them and operates across them. Your channels stay. The chaos goes." },
{ q: "Do I need a team?", a: "No. That's the point. Hunter is the team." },
];
return (
<section style={{ padding: "60px 0", borderTop: `1px solid ${T.border}` }}>
<div className="s">
<div style={{ maxWidth: 640, margin: "0 auto" }}>
{items.map((it, i) => (
<div key={i} style={{ padding: "14px 0", borderBottom: `1px solid ${T.border}` }}>
<p style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 4 }}>{it.q}</p>
<p style={{ fontSize: 12, color: T.text3, lineHeight: 1.5 }}>{it.a}</p>
</div>
))}
</div>
</div>
</section>
);
}

/* ═══════════════════════════════════════════
8. MANIFESTO
═══════════════════════════════════════════ */

function Manifesto() {
return (
<section style={{ padding: "60px 0", background: T.accent }}>
<div className="s" style={{ textAlign: "center" }}>
<Dots size={20} breathing={false} />
<p className="serif" style={{
fontSize: "clamp(18px,3vw,26px)", color: "rgba(255,255,255,0.80)",
lineHeight: 1.6, maxWidth: 540, margin: "20px auto 0",
}}>
Every business has a formula.{" "}
<span style={{ color: "rgba(255,255,255,0.95)" }}>Hunter finds yours. Mindhunters gives it one place to live.</span>
</p>
<p style={{ fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.50)", marginTop: 16, letterSpacing: "0.08em", textTransform: "uppercase" }}>
Be inevitable.
</p>
</div>
</section>
);
}

/* ═══════════════════════════════════════════
9. FINAL CTA
═══════════════════════════════════════════ */

function FinalCTA() {
return (
<section  style={{ padding: "80px 0 100px", textAlign: "center" }}>
<div className="s">
<h2 className="serif" style={{ fontSize: "clamp(28px,5vw,44px)", color: T.text1, marginBottom: 10 }}>
One place. One brain. Your business, running.
</h2>
<p style={{ fontSize: 14, color: T.text2, maxWidth: 420, margin: "0 auto 28px", lineHeight: 1.6 }}>
Free to start. No credit card. Hunter maps your business in 2 minutes.
</p>
<div style={{ display: "flex", justifyContent: "center" }}><AuthInput /></div>

  </div>
</section>

);
}

/* ═══════ FOOTER ═══════ */

function Footer() {
return (
<footer style={{ borderTop: `1px solid ${T.border}`, padding: "20px 0" }}>
<div className="s" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
<span style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, letterSpacing: "-0.02em" }}>© 2026 mindhunters</span>
<div style={{ display: "flex", gap: 16 }}>
<a href="/features" style={{ fontSize: 10, color: T.textMuted }}>Features</a>
<a href="/pricing" style={{ fontSize: 10, color: T.textMuted }}>Pricing</a>
<a href="/privacy" style={{ fontSize: 10, color: T.textMuted }}>Privacy</a>
<a href="/terms" style={{ fontSize: 10, color: T.textMuted }}>Terms</a>
<a href="mailto:hello@mindhunters.io" style={{ fontSize: 10, color: T.textMuted }}>Contact</a>
</div>
</div>
</footer>
);
}

/* ═══════════════════════════════════════════
MAIN — SECTION ORDER (conversion-first)
═══════════════════════════════════════════ */

export function MarketingSite() {
  const [authOpen, setAuthOpen] = useState(false);
  return (
    <div style={{ minHeight: "100vh", background: T.bg0 }}>
      <style>{css}</style>
      <Grain />
      <Nav />
      <Hero />
      <ProductPreview />
      <OnePlace />
      <TheFlow />
      <ProofPoints />
      <ConnectsTo />
      <PanelsSection />
      <Pillars />
      <PricingSection onStartFree={() => setAuthOpen(true)} />
      <Objections />
      <Manifesto />
      <FinalCTA />
      <Footer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
